import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useRoutes } from "react-router";
import Cookies from "js-cookie";
import { UserService } from "@/service";
import { Flex, Loader } from "@mantine/core";
import Layout from "@/component/Layout";
import { findRouteConfig, redirectParams } from "@/utils";
import qs from "qs";

// ------------------- 懒加载页面 -------------------
const Login = React.lazy(() => import("@/pages/Login"));
const Register = React.lazy(() => import("@/pages/Register"));
const Monitor = React.lazy(() => import("@/pages/Monitor"));
const Alerts = React.lazy(() => import("@/pages/Alerts"));
const Settings = React.lazy(() => import("@/pages/Settings"));
const Onboarding = React.lazy(() => import("@/pages/Onboarding"));
const Oauth = React.lazy(() => import("@/pages/Oauth"));
const CreateMonitor = React.lazy(() => import("@/pages/CreateMonitor"));
const EditMonitor = React.lazy(() => import("@/pages/EditMonitor"));
const Pricing = React.lazy(() => import("@/pages/Pricing"));
const PaymentSuccess = React.lazy(
  () => import("@/pages/Payment/PaymentSuccess")
);
const PaymentError = React.lazy(() => import("@/pages/Payment/PaymentError"));

// ------------------- 路由配置 -------------------
export const routerConfig = [
  {
    path: "/",
    private: true,
    element: <Layout />,
    children: [
      {
        index: true,
        element: <Navigate to="/monitor" replace />,
      },
      { path: "onboarding", private: true, element: <Onboarding /> },
      { path: "monitor", private: true, element: <Monitor /> },
      { path: "monitor_create", private: true, element: <CreateMonitor /> },
      { path: "monitor_edit", private: true, element: <EditMonitor /> },
      { path: "alerts", private: true, element: <Alerts /> },
      { path: "settings", private: true, element: <Settings /> },
      { path: "pricing", private: true, element: <Pricing /> },
      { path: "payment-success", private: true, element: <PaymentSuccess /> },
      { path: "payment-error", private: true, element: <PaymentError /> },
    ],
  },
  { path: "/oauth", private: false, element: <Oauth /> },
  { path: "/login", private: false, element: <Login /> },
  { path: "/register", private: false, element: <Register /> },
];

// ------------------- 路由守卫 -------------------
export default function RouterGuard() {
  const { pathname, search } = window.location;
  const navigate = useNavigate();
  const isLogin = !!Cookies.get("token");
  const matchedRoute = findRouteConfig(pathname, routerConfig);
  const { redirect } = qs.parse(search, {
    ignoreQueryPrefix: true,
  });

  const [user, setUserInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const qsParams = redirectParams(redirect);

  const getUserInfo = async () => {
    setLoading(true);
    try {
      const userInfo: any = await UserService();
      setUserInfo(userInfo.data);
      //先存一下 回显要用到  后续还的拉接口 现在懒得拉了
      localStorage.setItem("userInfo", JSON.stringify(userInfo.data));

      const NO_USER_PROFILE =
        !userInfo?.data?.industry ||
        (!userInfo?.data?.telPhone && !userInfo?.data?.contactEmail);
      const NO_SELECT_PRICING =
        userInfo?.data?.plan == "Free" && userInfo.data.planExpiresin == "0";
      //判断是不是过期
      const NO_EXPIRESION =
        Math.floor(Date.now() / 1000) > Number(userInfo.data.planExpiresin)
          ? true
          : false;

      if (NO_USER_PROFILE) {
        if (redirect && typeof redirect == "string") {
          navigate(`/onboarding?redirect=${encodeURIComponent(redirect)}`, {
            replace: true,
          });
          return;
        }
        navigate("/onboarding", { replace: true });
        return;
      }

      if (NO_SELECT_PRICING) {
        if (redirect && typeof redirect == "string") {
          navigate(`/pricing?redirect=${encodeURIComponent(redirect)}`, {
            replace: true,
          });
          return;
        }
        navigate("/pricing", { replace: true });
        return;
      }

      //如果套餐过期了
      if (NO_EXPIRESION && localStorage.getItem("AB_PRICING")) {
        navigate(`/pricing?planExpiresin=${userInfo.data.planExpiresin}`, {
          replace: true,
        });
        return;
      }

      if (qsParams) {
        const { auth, key } = qsParams;
        if (auth == "false" && key) {
          navigate(
            `/monitor_create?redirect=${encodeURIComponent(
              redirect as string
            )}`,
            { replace: true }
          );
          return;
        }
      }
    } catch (err) {
      Cookies.remove("token");
      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!matchedRoute || (matchedRoute?.private && isLogin)) {
      getUserInfo();
    }

    if (matchedRoute?.private && !isLogin) {
      navigate("/login", { replace: true });
    }
  }, []);

  const routes = useRoutes(routerConfig);

  if (matchedRoute?.private) {
    if (!isLogin) return null;
    if (loading || !user) {
      return (
        <Flex justify="center" align="center" style={{ height: "100vh" }}>
          <Loader size="lg" />
        </Flex>
      );
    }
  }

  return routes;
}
